import { BaseService } from './../../../core/services/base.service';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogTemplateComponent } from '@optsolservices/ngx-confirm-dialog';
import { HelperService } from 'src/app/core/services/helper.service';

@Component({
  selector: 'app-sales-order-line-discount-dialog',
  templateUrl: './sales-order-line-discount-dialog.component.html',
  styleUrls: ['./sales-order-line-discount-dialog.component.scss']
})
export class SalesOrderLineDiscountDialogComponent implements OnInit {
  discounts: {
    discountId: number;
    discountCode: string;
    discountDesc: string;
    discountType: string;
    discountValueFlag: string;
    discountMinValue: number;
    discountMaxValue: number;
    requiresApproval: boolean;
  }[] = [];

  form: FormGroup;

  constructor(
    private dialogRef: MatDialogRef<SalesOrderLineDiscountDialogComponent>,
    private baseService: BaseService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private helperService: HelperService
  ) {
    this.form = fb.group({
      salesOrderHeaderId: [null, Validators.compose([Validators.required])],
      lineNumber: [null, Validators.compose([Validators.required])],
      discountId: [null, Validators.compose([Validators.required])],
      discountValue: [null, Validators.compose([Validators.required])],
      approvedByEmail: [null],
      approvedByPwd: [null],

      stockCode: [null],
      lineDescription: [null],
      lineDescription2: [null],
      discountMinValue: [null],
      discountMaxValue: [null],
      discountType: [null],
      discountValueFlag: [null],
      contract: [null],
      unitRspIncludingVat: [null],
      requiresApproval: [false]
    });

    this.form.controls['salesOrderHeaderId'].disable();
    this.form.controls['lineNumber'].disable();
    this.form.controls['stockCode'].disable();
    this.form.controls['lineDescription'].disable();
    this.form.controls['lineDescription2'].disable();
    this.form.controls['discountMinValue'].disable();
    this.form.controls['discountMaxValue'].disable();
    this.form.controls['discountType'].disable();
    this.form.controls['discountValueFlag'].disable();
    this.form.controls['contract'].disable();
    this.form.controls['unitRspIncludingVat'].disable();

    this.form.patchValue(this.data);

    if (this.data && this.data.discountPercentage > 0) {
      this.form.controls['discountValue'].setValue(this.data.discountPercentage);
    }
  }

  ngOnInit() {
    this.baseService.executeCommand<any>('portal_sales_order_line_discount_dialog_config', this.data)
      .subscribe(response => {
        if (response.responseCode === 0) {
          this.discounts = response.data.discounts || [];
          const currentDiscount = this.discounts.find(f => f.discountCode === this.data.discountReasonCode);
          if (currentDiscount) {
            this.form.controls['discountId'].setValue(currentDiscount.discountId);
            this.discountChanged(currentDiscount.discountId);
          } else {
            this.discountChanged(null);
          }
        } else {
          this.discounts = [];
        }
      });
  }

  discountChanged(discountId: number) {
    this.form.controls['discountMinValue'].setValue(null);
    this.form.controls['discountMaxValue'].setValue(null);
    this.form.controls['discountType'].setValue(null);
    this.form.controls['discountValueFlag'].setValue(null);
    this.form.controls['approvedByEmail'].setValue(null);
    this.form.controls['approvedByPwd'].setValue(null);
    this.form.controls['requiresApproval'].setValue(false);

    this.form.controls['discountValue'].setValidators(Validators.compose([Validators.required]));
    this.form.controls['approvedByEmail'].clearValidators();
    this.form.controls['approvedByPwd'].clearValidators();

    if (discountId) {
      const discount = this.discounts.find(f => f.discountId === discountId);
      this.form.controls['discountMinValue'].setValue(discount.discountMinValue);
      this.form.controls['discountMaxValue'].setValue(discount.discountMaxValue);
      this.form.controls['discountType'].setValue(discount.discountType);
      this.form.controls['discountValueFlag'].setValue(discount.discountValueFlag);
      this.form.controls['requiresApproval'].setValue(discount.requiresApproval);

      this.form.controls['discountValue'].setValidators(Validators.compose([Validators.required, Validators.min(discount.discountMinValue), Validators.max(discount.discountMaxValue)]))
      if (discount.requiresApproval) {
        this.form.controls['approvedByEmail'].setValidators(Validators.compose([Validators.required, Validators.email]));
        this.form.controls['approvedByPwd'].setValidators(Validators.compose([Validators.required]));
      } else {
        this.form.controls['approvedByEmail'].clearValidators();
        this.form.controls['approvedByPwd'].clearValidators();
        this.form.controls['approvedByEmail'].updateValueAndValidity();
        this.form.controls['approvedByPwd'].updateValueAndValidity();
      }

    }

    this.form.controls['discountValue'].updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  save() {
    let body = this.form.getRawValue();

    if (this.form.value.requiresApproval) {
      body.approvedByPwd = this.helperService.hash(body.approvedByPwd);
    }

    this.baseService.executeCommand<any>('portal_sales_order_line_discount_modify', body)
      .subscribe(response => {
        if (response.responseCode === 0) {
          this.dialogRef.close(true);
        }
      });
  }

  clearDiscount() {
    const dialogRef = this.dialog.open(ConfirmDialogTemplateComponent, {
      width: '450px',
      data: {
        title: 'Reset Price and Discount?',
        text: 'Are you sure you want to reset the price and discount on this line? This cannot be undone. The price will either be retrieved from SYSPRO or from a parent Quote if one exists.',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showConfirmButton: true,
        showCancelButton: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.confirmButtonClicked === true) {
        this.baseService.executeCommand<any>('portal_sales_order_line_price_reset', this.data)
          .subscribe(response => {
            if (response.responseCode === 0) {
              this.dialogRef.close(true);
            }
          });
      }
    });
  }
}
